// frontend/src/components/Financeiro/ContabilidadeIVA.js
// Apuramento de IVA detalhado — lê dos endpoints do DashboardContabilidade
// ✅ PERSONALIZAÇÃO DE MOEDA E FORMATAÇÃO DINÂMICA
// ✅ FILTRO MENSAL / ANUAL (TODOS OS MESES JUNTOS)
// ✅ TABELA DETALHADA MÊS A MÊS
// ✅ GRÁFICO IVA POR TAXA (MENSAL) OU IVA POR MÊS (ANUAL)
// ✅ DECOMPOSIÇÃO DO IVA DEDUTÍVEL (Despesas + Compras de Stock)
// 🔥 REGIME FISCAL:
//    - Normal: IVA 16% cobrado mensalmente → IVA Liquidado − IVA Dedutível
//    - ISPC:   IVA 0% nas faturas → Paga 15% do Volume de Negócios TRIMESTRALMENTE (Mar/Jun/Set/Dez)
//    - Isento: sem submissão
// 🔥 CONTROLO DE SUBMISSÕES SINCRONIZADO COM DashboardContabilidade:
//    - Usa os MESMOS 5 ESTADOS que o Dashboard
//    - Busca estado real das submissões por período (Mensal para Normal, Trimestral para ISPC)
//    - Cada linha de trimestre/mês mostra badge com estado (Pendente/Submetido/Pago/Falta Comprovativo/Completo)
//    - Botão de ação por período para abrir modal de submissão
//    - Se já submetido → Editar
//    - Se pago SEM comprovativo → "Falta Comprovativo" (azul)
//    - Se pago + comprovativo → Completo (bloqueado)
// 🔥 SINCRONIZAÇÃO: usa o MESMO endpoint `/api/contabilidade/fechos` que o Dashboard
// 🔥 CORREÇÃO: getFechoPorTrimestre agora procura em QUALQUER mês do trimestre
//    (o fecho pode estar em Julho, Agosto OU Setembro — não necessariamente Setembro)
// 🔥 CORREÇÃO CRÍTICA: carregarFechos agora lê `res.data.fechos` (não `res.data.data`)
//    — o backend devolve { success: true, fechos: [...] }
// 🔥 NOVO: configuracao (moeda+formatacao) + empresa vêm diretamente do backend
//    (resumo, comparativo-anual, fechos) — sem chamadas duplicadas a /api/empresas/minha/atual
// =============================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Icon } from './SharedComponents';
import {
    FaUpload, FaFilePdf, FaExternalLinkAlt, FaCheckCircle, FaTimes,
    FaExclamationTriangle, FaInfoCircle, FaQuestionCircle, FaEdit, FaLock
} from 'react-icons/fa';

const API = '/api' || 'http://localhost:3000';

const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const mesesAbrev = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const MESES_FIM_TRIMESTRE = [2, 5, 8, 11];
const NOMES_FIM_TRIMESTRE = ['Março', 'Junho', 'Setembro', 'Dezembro'];

const trimestreDoMes = (mes) => Math.floor(mes / 3) + 1;
const isMesFimTrimestre = (mes) => MESES_FIM_TRIMESTRE.includes(mes);

const TAXA_ISPC = 0.15;
const TAXA_ISPC_LABEL = '15%';

const TIPO_IMPOSTO_POR_REGIME = {
    Normal: 'IVA',
    ISPC: 'ISPC',
    Isento: 'Nenhum'
};

const DESCRICAO_IMPOSTO = {
    IVA: 'Imposto sobre o Valor Acrescentado',
    ISPC: 'Imposto Simplificado para Pequenos Contribuintes',
    Nenhum: 'Sem imposto associado'
};

const PERIODICIDADE_POR_REGIME = {
    Normal: 'Mensal',
    ISPC: 'Trimestral',
    Isento: '—'
};

// 🔥 CONFIGURAÇÃO PADRÃO (fallback)
const CONFIG_PADRAO = {
    regimeIVA: 'Normal',
    percentagemIVA: 16,
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.',
        posicaoSimbolo: 'depois',
        nome: 'Metical'
    },
    formatacao: {
        data: { formato: 'DD/MM/YYYY', separador: '/', incluirHora: false },
        numero: { separadorDecimal: ',', separadorMilhar: '.', casasDecimais: 2 }
    }
};

// =============================================
// 🔥 ESTADOS DA SUBMISSÃO — MESMOS 5 DO DashboardContabilidade
// =============================================
const ESTADOS_SUBMISSAO = {
    SEM_SUBMISSAO: 'sem_submissao',
    PENDENTE: 'pendente',
    SUBMETIDO: 'submetido',
    PAGO_SEM_COMPROVATIVO: 'pago_sem_comprovativo',
    PAGO_COMPLETO: 'pago_completo'
};

const ESTADO_BADGE_MAP = {
    pendente: { label: 'Pendente', classe: 'badge-warning', icone: 'schedule' },
    submetido: { label: 'Submetido', classe: 'badge-info', icone: 'send' },
    pago_sem_comprovativo: { label: 'Falta Comprovativo', classe: 'badge-warning', icone: 'cloud_upload' },
    pago_completo: { label: 'Completo', classe: 'badge-success', icone: 'verified' },
    sem_submissao: { label: 'Sem Submissão', classe: 'badge-muted', icone: 'hourglass_empty' }
};

const ESTADO_TITULO_MODAL = {
    [ESTADOS_SUBMISSAO.SEM_SUBMISSAO]: 'Registar Submissão às Finanças',
    [ESTADOS_SUBMISSAO.PENDENTE]: 'Editar Submissão Pendente',
    [ESTADOS_SUBMISSAO.SUBMETIDO]: 'Editar Submissão Pendente',
    [ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO]: 'Anexar Comprovativo de Pagamento',
    [ESTADOS_SUBMISSAO.PAGO_COMPLETO]: 'Submissão Completa'
};

// 🔥 Evento global para sincronizar componentes
const SUBMISSAO_ATUALIZADA_EVENT = 'contabilidade:submissao-atualizada';

export default function ContabilidadeIVA() {
    const [ano, setAno] = useState(new Date().getFullYear());
    const [mes, setMes] = useState(new Date().getMonth());
    const [tipo, setTipo] = useState('Mensal');
    const [dados, setDados] = useState(null);
    const [detalheMensal, setDetalheMensal] = useState([]);
    const [loading, setLoading] = useState(true);

    // 🔥 Estado dos fechos/submissões por período (SINCRONIZADO com Dashboard)
    const [fechos, setFechos] = useState([]);

    // 🔥 Estado dos popups do sistema
    const [popup, setPopup] = useState({
        aberto: false,
        tipo: 'info',
        titulo: '',
        mensagem: '',
        textoConfirmar: 'Confirmar',
        textoCancelar: 'Cancelar',
        onConfirmar: null,
        onCancelar: null
    });

    const fecharPopup = useCallback(() => {
        setPopup(prev => ({ ...prev, aberto: false }));
    }, []);

    const mostrarPopup = useCallback((tipo, titulo, mensagem) => {
        setPopup({
            aberto: true,
            tipo,
            titulo,
            mensagem,
            textoConfirmar: 'OK',
            textoCancelar: null,
            onConfirmar: () => fecharPopup(),
            onCancelar: null
        });
    }, [fecharPopup]);

    const confirmarPopup = useCallback((titulo, mensagem, onConfirmar, opcoes = {}) => {
        setPopup({
            aberto: true,
            tipo: opcoes.tipo || 'confirm',
            titulo,
            mensagem,
            textoConfirmar: opcoes.textoConfirmar || 'Confirmar',
            textoCancelar: opcoes.textoCancelar || 'Cancelar',
            onConfirmar: () => {
                fecharPopup();
                if (onConfirmar) onConfirmar();
            },
            onCancelar: () => {
                fecharPopup();
                if (opcoes.onCancelar) opcoes.onCancelar();
            }
        });
    }, [fecharPopup]);

    // 🔥 Estados do modal de submissão
    const [modalSubmissaoAberto, setModalSubmissaoAberto] = useState(false);
    const [periodoSelecionado, setPeriodoSelecionado] = useState(null);
    const [submissaoForm, setSubmissaoForm] = useState({
        referencia: '',
        dataSubmissao: new Date().toISOString().slice(0, 10),
        dataPagamento: '',
        valorPago: '',
        estado: 'Submetido',
        observacoes: ''
    });
    const [submissaoFicheiro, setSubmissaoFicheiro] = useState(null);
    const [submetendo, setSubmetendo] = useState(false);

    // 🔥 Config da empresa — começa com padrão, é atualizada pelas respostas
    const [empresaConfig, setEmpresaConfig] = useState(CONFIG_PADRAO);

    const token = localStorage.getItem('token');
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    // =============================================
    // 🔥 DERIVADOS DO REGIME
    // =============================================
    const regimeIVA = empresaConfig.regimeIVA || 'Normal';
    const isNormal = regimeIVA === 'Normal';
    const isISPC = regimeIVA === 'ISPC';
    const isIsento = regimeIVA === 'Isento';

    const tipoImposto = TIPO_IMPOSTO_POR_REGIME[regimeIVA] || 'IVA';
    const descricaoImposto = DESCRICAO_IMPOSTO[tipoImposto] || '';
    const periodicidadeImposto = PERIODICIDADE_POR_REGIME[regimeIVA] || 'Mensal';

    const periodicidadeLabel = isISPC ? 'Trimestral' : isNormal ? 'Mensal' : 'Isento';
    const taxaLabel = isNormal
        ? `${empresaConfig.percentagemIVA ?? 16}%`
        : isISPC
            ? `${TAXA_ISPC_LABEL} (trimestral)`
            : '—';

    const trimestreAtual = trimestreDoMes(mes);
    const deveSubmeterEsteMes = isNormal || (isISPC && isMesFimTrimestre(mes));

    useEffect(() => {
        if ((isISPC || isIsento) && tipo === 'Mensal') {
            setTipo('Anual');
        }
    }, [isISPC, isIsento, tipo]);

    // =============================================
    // 🔥 FORMATAR MOEDA (DINÂMICA)
    // =============================================
    const fmt = useMemo(() => {
        return (valor) => {
            const cfg = empresaConfig.moeda || CONFIG_PADRAO.moeda;
            const sepDec = cfg.separadorDecimal || ',';
            const sepMil = cfg.separadorMilhar || '.';
            const casasDec = cfg.casasDecimais ?? 2;
            const simbolo = cfg.simbolo || 'MT';

            if (valor === undefined || valor === null || isNaN(valor)) {
                const zeros = '0'.repeat(casasDec);
                return `0${sepDec}${zeros} ${simbolo}`;
            }

            let valorFormatado = Number(valor).toFixed(casasDec);
            valorFormatado = valorFormatado.replace('.', sepDec);

            const partes = valorFormatado.split(sepDec);
            partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
            valorFormatado = partes.join(sepDec);

            return `${valorFormatado} ${simbolo}`;
        };
    }, [empresaConfig.moeda]);

    const formatarData = useCallback((data) => {
        if (!data) return '-';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '-';

        const cfg = empresaConfig.formatacao?.data || CONFIG_PADRAO.formatacao.data;
        let formato = cfg.formato || 'DD/MM/YYYY';
        const dia = String(d.getDate()).padStart(2, '0');
        const mesNum = String(d.getMonth() + 1).padStart(2, '0');
        const anoNum = d.getFullYear();

        formato = formato.replace('DD', dia);
        formato = formato.replace('MM', mesNum);
        formato = formato.replace('YYYY', anoNum);
        formato = formato.replace('YY', String(anoNum).slice(-2));

        return formato;
    }, [empresaConfig.formatacao]);

    // =============================================
    // 🔥 APLICAR CONFIGURAÇÃO DA EMPRESA (helper)
    // =============================================
    const aplicarConfiguracao = useCallback((resData) => {
        if (!resData) return;
        const cfg = resData.configuracao;
        const emp = resData.empresa;

        if (cfg || emp) {
            setEmpresaConfig(prev => ({
                ...prev,
                regimeIVA: emp?.regimeIVA ?? prev.regimeIVA,
                percentagemIVA: emp?.percentagemIVA ?? prev.percentagemIVA,
                moeda: cfg?.moeda || emp?.moeda || prev.moeda,
                formatacao: cfg?.formatacao || emp?.formatacao || prev.formatacao
            }));
        }
    }, []);

    // =============================================
    // 🔥 BUSCAR FECHOS DO ANO (FONTE ÚNICA DE VERDADE)
    // ✅ Backend devolve { success, fechos } — NÃO { data }
    // =============================================
    const carregarFechos = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/contabilidade/fechos`, {
                params: { ano },
                headers
            });
            if (res.data?.success) {
                // 🔥 Aplicar config da resposta
                aplicarConfiguracao(res.data);

                const lista = res.data.fechos || [];
                console.log('🔍 [ContabilidadeIVA] Fechos carregados:', lista.length, 'para ano', ano);
                setFechos(lista);
                return lista;
            }
            return [];
        } catch (e) {
            console.error('❌ Erro ao carregar fechos:', e);
            return [];
        }
    }, [ano, headers, aplicarConfiguracao]);

    // =============================================
    // 🔥 HELPERS — encontrar fecho/submissão por período
    // =============================================
    const getFechoPorMes = useCallback((mesIdx) => {
        return fechos.find(f => Number(f.mes) === mesIdx && Number(f.ano) === ano);
    }, [fechos, ano]);

    const getFechoPorTrimestre = useCallback((trimestre) => {
        const mesInicio = (trimestre - 1) * 3;
        const mesFim = trimestre * 3 - 1;
        const fecho = fechos.find(f => {
            const m = Number(f.mes);
            return m >= mesInicio && m <= mesFim && Number(f.ano) === ano;
        });
        return fecho;
    }, [fechos, ano]);

    // =============================================
    // 🔥 ESTADO DA SUBMISSÃO — 5 ESTADOS
    // =============================================
    const estadoDoFecho = useCallback((fecho) => {
        if (!fecho) return ESTADOS_SUBMISSAO.SEM_SUBMISSAO;
        const sub = fecho.submissaoFinancas;
        if (!sub || !sub.estado) return ESTADOS_SUBMISSAO.SEM_SUBMISSAO;

        const estadoLower = String(sub.estado).toLowerCase();
        const temComprovativo = !!sub.comprovativoUrl;

        if (estadoLower === 'pago' && temComprovativo) return ESTADOS_SUBMISSAO.PAGO_COMPLETO;
        if (estadoLower === 'pago' && !temComprovativo) return ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO;
        if (estadoLower === 'submetido') return ESTADOS_SUBMISSAO.SUBMETIDO;
        if (estadoLower === 'pendente') return ESTADOS_SUBMISSAO.PENDENTE;
        return ESTADOS_SUBMISSAO.PENDENTE;
    }, []);

    const estadoVisual = useCallback((estado) => {
        if (estado === ESTADOS_SUBMISSAO.PAGO_COMPLETO) return ESTADO_BADGE_MAP.pago_completo;
        if (estado === ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO) return ESTADO_BADGE_MAP.pago_sem_comprovativo;
        if (estado === ESTADOS_SUBMISSAO.SUBMETIDO) return ESTADO_BADGE_MAP.submetido;
        if (estado === ESTADOS_SUBMISSAO.PENDENTE) return ESTADO_BADGE_MAP.pendente;
        return ESTADO_BADGE_MAP.sem_submissao;
    }, []);

    // =============================================
    // 🔥 CARREGAR DADOS IVA (sem chamada a /empresas/minha/atual)
    // =============================================
    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            // 1. Fechos do ano (SINCRONIZADO com Dashboard)
            await carregarFechos();

            // 2. Resumo (Mensal ou consolidado de 12 meses)
            if (tipo === 'Mensal' && !isISPC && !isIsento) {
                const res = await axios.get(`${API}/contabilidade/resumo`, {
                    params: { ano, mes, tipo: 'Mensal' },
                    headers
                });
                if (res.data.success) {
                    setDados(res.data);
                    setDetalheMensal([]);
                    // 🔥 Aplicar config
                    aplicarConfiguracao(res.data);
                }
            } else {
                const promessas = meses.map((_, index) =>
                    axios.get(`${API}/contabilidade/resumo`, {
                        params: { ano, mes: index, tipo: 'Mensal' },
                        headers
                    })
                );

                const respostas = await Promise.all(promessas);

                // 🔥 Aplicar config da primeira resposta válida
                for (const r of respostas) {
                    if (r.data?.success && r.data.configuracao) {
                        aplicarConfiguracao(r.data);
                        break;
                    }
                }

                const detalhe = respostas.map((res, index) => {
                    const d = res.data?.dados || {};
                    const ap = d.apuramentoIVA || {};
                    const rec = d.receitas || {};
                    return {
                        mesIndex: index,
                        mesNome: meses[index],
                        mesAbrev: mesesAbrev[index],
                        trimestre: trimestreDoMes(index),
                        isFimTrimestre: isMesFimTrimestre(index),
                        ivaLiquidado: ap.ivaLiquidado || 0,
                        ivaDedutivel: ap.ivaDedutivel || 0,
                        ivaDedutivelDespesas: ap.ivaDedutivelDespesas || 0,
                        ivaDedutivelCompras: ap.ivaDedutivelCompras || 0,
                        ivaAEntregar: ap.ivaAEntregar || 0,
                        ivaARecuperar: ap.ivaARecuperar || 0,
                        baseTributavel: rec.baseTributavel || 0,
                        numeroFaturas: rec.numeroFaturas || 0
                    };
                });
                setDetalheMensal(detalhe);

                const totais = detalhe.reduce((acc, m) => {
                    acc.ivaLiquidado += m.ivaLiquidado;
                    acc.ivaDedutivel += m.ivaDedutivel;
                    acc.ivaDedutivelDespesas += m.ivaDedutivelDespesas;
                    acc.ivaDedutivelCompras += m.ivaDedutivelCompras;
                    acc.ivaAEntregar += m.ivaAEntregar;
                    acc.ivaARecuperar += m.ivaARecuperar;
                    acc.baseTributavel += m.baseTributavel;
                    acc.numeroFaturas += m.numeroFaturas;
                    return acc;
                }, {
                    ivaLiquidado: 0,
                    ivaDedutivel: 0,
                    ivaDedutivelDespesas: 0,
                    ivaDedutivelCompras: 0,
                    ivaAEntregar: 0,
                    ivaARecuperar: 0,
                    baseTributavel: 0,
                    numeroFaturas: 0
                });

                let totaisISPC = null;
                if (isISPC) {
                    totaisISPC = {
                        volumeNegocios: totais.baseTributavel,
                        impostoTotalAno: 0,
                        numeroFaturas: totais.numeroFaturas,
                        trimestres: [
                            { trimestre: 1, volume: 0, imposto: 0, fechado: false, submetido: false },
                            { trimestre: 2, volume: 0, imposto: 0, fechado: false, submetido: false },
                            { trimestre: 3, volume: 0, imposto: 0, fechado: false, submetido: false },
                            { trimestre: 4, volume: 0, imposto: 0, fechado: false, submetido: false }
                        ]
                    };

                    detalhe.forEach(m => {
                        const tri = totaisISPC.trimestres[m.trimestre - 1];
                        tri.volume += m.baseTributavel;
                        if (m.isFimTrimestre) {
                            tri.fechado = true;
                            tri.imposto = tri.volume * TAXA_ISPC;
                            totaisISPC.impostoTotalAno += tri.imposto;
                        }
                    });
                }

                const taxasMap = {};
                respostas.forEach(res => {
                    const taxas = res.data?.dados?.receitas?.apuramentoIVAPorTaxa || [];
                    taxas.forEach(t => {
                        if (!taxasMap[t.taxa]) {
                            taxasMap[t.taxa] = { taxa: t.taxa, base: 0, iva: 0, numeroFaturas: 0 };
                        }
                        taxasMap[t.taxa].base += t.base || 0;
                        taxasMap[t.taxa].iva += t.iva || 0;
                        taxasMap[t.taxa].numeroFaturas += t.numeroFaturas || 0;
                    });
                });
                const ivaPorTaxaConsolidado = Object.values(taxasMap).map(t => ({
                    taxa: t.taxa,
                    base: parseFloat(t.base.toFixed(2)),
                    iva: parseFloat(t.iva.toFixed(2)),
                    numeroFaturas: t.numeroFaturas
                })).sort((a, b) => a.taxa - b.taxa);

                const catMap = {};
                respostas.forEach(res => {
                    const cats = res.data?.dados?.despesas?.porCategoria || [];
                    cats.forEach(c => {
                        const nome = c.categoria || 'Outros';
                        if (!catMap[nome]) catMap[nome] = { categoria: nome, valor: 0 };
                        catMap[nome].valor += c.valor || 0;
                    });
                });
                const despesasPorCatConsolidado = Object.values(catMap).map(c => ({
                    categoria: c.categoria,
                    valor: parseFloat(c.valor.toFixed(2))
                })).sort((a, b) => b.valor - a.valor);

                if (isISPC) {
                    setDados({
                        success: true,
                        dados: {
                            regimeIVA,
                            receitas: {
                                baseTributavel: parseFloat(totais.baseTributavel.toFixed(2)),
                                numeroFaturas: totais.numeroFaturas,
                                apuramentoIVAPorTaxa: []
                            },
                            despesas: {
                                porCategoria: despesasPorCatConsolidado,
                                ivaDedutivel: parseFloat(totais.ivaDedutivelDespesas.toFixed(2))
                            },
                            apuramentoIVA: {
                                ivaLiquidado: 0,
                                ivaDedutivel: 0,
                                ivaDedutivelDespesas: 0,
                                ivaDedutivelCompras: 0,
                                ivaAEntregar: parseFloat(totaisISPC.impostoTotalAno.toFixed(2)),
                                ivaARecuperar: 0,
                                volumeNegocios: parseFloat(totaisISPC.volumeNegocios.toFixed(2)),
                                taxaISPC: TAXA_ISPC * 100,
                                trimestres: totaisISPC.trimestres
                            }
                        }
                    });
                } else {
                    setDados({
                        success: true,
                        dados: {
                            regimeIVA,
                            receitas: {
                                baseTributavel: parseFloat(totais.baseTributavel.toFixed(2)),
                                numeroFaturas: totais.numeroFaturas,
                                apuramentoIVAPorTaxa: ivaPorTaxaConsolidado
                            },
                            despesas: {
                                porCategoria: despesasPorCatConsolidado,
                                ivaDedutivel: parseFloat(totais.ivaDedutivelDespesas.toFixed(2))
                            },
                            apuramentoIVA: {
                                ivaLiquidado: parseFloat(totais.ivaLiquidado.toFixed(2)),
                                ivaDedutivel: parseFloat(totais.ivaDedutivel.toFixed(2)),
                                ivaDedutivelDespesas: parseFloat(totais.ivaDedutivelDespesas.toFixed(2)),
                                ivaDedutivelCompras: parseFloat(totais.ivaDedutivelCompras.toFixed(2)),
                                ivaAEntregar: parseFloat(totais.ivaAEntregar.toFixed(2)),
                                ivaARecuperar: parseFloat(totais.ivaARecuperar.toFixed(2))
                            }
                        }
                    });
                }
            }
        } catch (e) {
            console.error('❌ Erro carregar IVA:', e);
        } finally {
            setLoading(false);
        }
    }, [ano, mes, tipo, isISPC, isIsento, regimeIVA, headers, carregarFechos, aplicarConfiguracao]);

    useEffect(() => { carregar(); }, [carregar]);

    // 🔥 Recarregar fechos quando o ANO muda
    useEffect(() => {
        carregarFechos();
    }, [ano, carregarFechos]);

    // =============================================
    // 🔥 SINCRONIZAÇÃO COM O DashboardContabilidade
    // =============================================
    useEffect(() => {
        const handler = () => {
            console.log('🔄 [ContabilidadeIVA] Submissão atualizada noutro componente — recarregando fechos...');
            carregarFechos();
        };
        window.addEventListener(SUBMISSAO_ATUALIZADA_EVENT, handler);
        return () => window.removeEventListener(SUBMISSAO_ATUALIZADA_EVENT, handler);
    }, [carregarFechos]);

    const d = dados?.dados || {};
    const r = d.receitas || {};
    const des = d.despesas || {};
    const ap = d.apuramentoIVA || {};
    const ivaPorTaxa = r.apuramentoIVAPorTaxa || [];
    const despesasPorCat = des.porCategoria || [];
    const trimestresISPC = ap.trimestres || [];

    // =============================================
    // 🔥 DADOS PARA O GRÁFICO
    // =============================================
    const dadosGrafico = useMemo(() => {
        if (isISPC) {
            return detalheMensal.map(m => ({
                label: m.mesAbrev,
                base: m.baseTributavel,
                iva: m.isFimTrimestre ? m.baseTributavel * TAXA_ISPC : 0
            }));
        }
        if (tipo === 'Anual') {
            return detalheMensal.map(m => ({
                label: m.mesAbrev,
                base: m.baseTributavel,
                iva: m.ivaLiquidado,
                ivaDedutivel: m.ivaDedutivel
            }));
        }
        return ivaPorTaxa.map(t => ({
            label: `${t.taxa}%`,
            base: t.base,
            iva: t.iva
        }));
    }, [isISPC, tipo, detalheMensal, ivaPorTaxa]);

    const tituloGrafico = isISPC
        ? `Volume de Negócios e Imposto ISPC por Mês (${ano})`
        : tipo === 'Anual'
            ? `IVA Liquidado por Mês (${ano})`
            : 'IVA por Taxa';

    const subtituloPeriodo = isISPC
        ? `Ano ${ano} (regime ISPC — trimestral ${TAXA_ISPC_LABEL})`
        : tipo === 'Anual'
            ? `Ano ${ano} (todos os meses consolidados)`
            : `${meses[mes]} ${ano}`;

    // =============================================
    // 🔥 ABRIR MODAL DE SUBMISSÃO PARA UM PERÍODO
    // =============================================
    const abrirModalSubmissaoPeriodo = (fecho, tipoPeriodoLabel) => {
        if (isIsento) {
            mostrarPopup('info', 'Regime Isento', 'Empresas no regime Isento não têm submissão de IVA.');
            return;
        }

        if (!fecho) {
            mostrarPopup('warning', 'Período não fechado',
                'É preciso fechar o período no Painel Contabilístico antes de registar a submissão.');
            return;
        }

        const estado = estadoDoFecho(fecho);

        if (estado === ESTADOS_SUBMISSAO.PAGO_COMPLETO) {
            mostrarPopup('info', 'Submissão Completa',
                'Esta submissão já está paga e com comprovativo. Não pode ser editada.');
            return;
        }

        const sub = fecho.submissaoFinancas || {};

        // Cálculo do valor por período
        let valorCalculado = 0;
        if (isISPC) {
            const mesFim = Number(fecho.mes);
            const tri = Math.floor(mesFim / 3) + 1;
            const mesesIdx = [tri * 3 - 3, tri * 3 - 2, tri * 3 - 1];
            const volumeTri = detalheMensal
                .filter(m => mesesIdx.includes(m.mesIndex))
                .reduce((s, m) => s + (m.baseTributavel || 0), 0);
            valorCalculado = Number((volumeTri * TAXA_ISPC).toFixed(2));
        } else {
            const dMes = fecho?.dados?.apuramentoIVA || {};
            valorCalculado = Number((dMes.ivaAEntregar || 0).toFixed(2));
        }

        const valorPreenchido = sub.valorPago ? sub.valorPago : valorCalculado;

        const refDefault = isISPC
            ? `ISPC-${ano}-T${Math.floor(Number(fecho.mes) / 3) + 1}`
            : `IVA-${ano}-${String(Number(fecho.mes) + 1).padStart(2, '0')}`;

        let estadoInicial = sub.estado || 'Submetido';
        if (estado === ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO) {
            estadoInicial = 'Pago';
        }

        setPeriodoSelecionado({
            fecho,
            tipoPeriodoLabel,
            valorCalculado,
            estado
        });

        setSubmissaoForm({
            referencia: sub.referencia || refDefault,
            dataSubmissao: sub.dataSubmissao
                ? new Date(sub.dataSubmissao).toISOString().slice(0, 10)
                : new Date().toISOString().slice(0, 10),
            dataPagamento: sub.dataPagamento
                ? new Date(sub.dataPagamento).toISOString().slice(0, 10)
                : '',
            valorPago: valorPreenchido,
            estado: estadoInicial,
            observacoes: sub.observacoes || ''
        });
        setSubmissaoFicheiro(null);
        setModalSubmissaoAberto(true);
    };

    // =============================================
    // 🔥 REGISTAR / EDITAR SUBMISSÃO
    // =============================================
    const registarSubmissao = async () => {
        if (!periodoSelecionado?.fecho?._id) {
            mostrarPopup('warning', 'Erro', 'Período inválido.');
            return;
        }

        if (!submissaoForm.referencia || submissaoForm.referencia.trim() === '') {
            mostrarPopup('warning', 'Campo obrigatório', 'A referência / nº da guia é obrigatória.');
            return;
        }

        if (!submissaoForm.dataSubmissao) {
            mostrarPopup('warning', 'Campo obrigatório', 'A data de submissão é obrigatória.');
            return;
        }

        const estadoFormLower = String(submissaoForm.estado).toLowerCase();
        const temComprovativoExistente = !!periodoSelecionado.fecho?.submissaoFinancas?.comprovativoUrl;
        const temNovoComprovativo = !!submissaoFicheiro;

        if (estadoFormLower === 'pago' && !temComprovativoExistente && !temNovoComprovativo) {
            mostrarPopup(
                'warning',
                'Comprovativo obrigatório',
                'Para marcar como PAGO é obrigatório anexar o comprovativo de pagamento.\n\n' +
                'Anexe o ficheiro ou altere o estado para "Submetido".'
            );
            return;
        }

        const isEdicao = periodoSelecionado.estado !== ESTADOS_SUBMISSAO.SEM_SUBMISSAO;
        const faltaComp = periodoSelecionado.estado === ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO;

        let descricaoAcao = 'registar';
        if (faltaComp) descricaoAcao = 'anexar comprovativo e completar';
        else if (isEdicao) descricaoAcao = 'atualizar';

        confirmarPopup(
            isEdicao ? 'Confirmar Edição' : 'Confirmar Submissão',
            `Confirma ${descricaoAcao} da submissão de ${tipoImposto} do período ${periodoSelecionado.tipoPeriodoLabel}?\n\n` +
            `Referência: ${submissaoForm.referencia}\n` +
            `Valor: ${fmt(submissaoForm.valorPago || 0)}\n` +
            `Estado: ${submissaoForm.estado}` +
            (temNovoComprovativo ? `\n\n📎 Comprovativo: ${submissaoFicheiro.name}` : ''),
            async () => {
                setSubmetendo(true);
                try {
                    const formData = new FormData();
                    formData.append('referencia', submissaoForm.referencia);
                    formData.append('dataSubmissao', submissaoForm.dataSubmissao);
                    if (submissaoForm.dataPagamento) formData.append('dataPagamento', submissaoForm.dataPagamento);
                    formData.append('valorPago', submissaoForm.valorPago || '0');
                    formData.append('estado', submissaoForm.estado);
                    formData.append('observacoes', submissaoForm.observacoes);
                    formData.append('tipoImposto', tipoImposto);
                    formData.append('regimeIVA', regimeIVA);
                    if (submissaoFicheiro) formData.append('comprovativo', submissaoFicheiro);

                    await axios.put(
                        `${API}/contabilidade/fechos/${periodoSelecionado.fecho._id}/submissao`,
                        formData,
                        {
                            headers: {
                                ...headers,
                                'Content-Type': 'multipart/form-data'
                            }
                        }
                    );

                    setModalSubmissaoAberto(false);
                    setSubmissaoFicheiro(null);

                    let tituloSucesso = isEdicao ? 'Submissão Atualizada' : 'Submissão Registada';
                    let msgSucesso = `A submissão de ${tipoImposto} do período ${periodoSelecionado.tipoPeriodoLabel} foi ${isEdicao ? 'atualizada' : 'registada'} com sucesso.`;

                    if (faltaComp) {
                        tituloSucesso = 'Comprovativo Anexado';
                        msgSucesso = `O comprovativo foi anexado com sucesso. A submissão de ${tipoImposto} do período ${periodoSelecionado.tipoPeriodoLabel} está agora completa.`;
                    }

                    mostrarPopup('success', tituloSucesso, msgSucesso);

                    await carregarFechos();
                    await carregar();

                    // 🔥 Sincronização global
                    window.dispatchEvent(new CustomEvent(SUBMISSAO_ATUALIZADA_EVENT, {
                        detail: {
                            fechoId: periodoSelecionado.fecho._id,
                            estado: submissaoForm.estado,
                            ano
                        }
                    }));
                } catch (e) {
                    mostrarPopup('danger', 'Erro ao Registar',
                        e.response?.data?.message || 'Erro ao registar submissão. Tente novamente.');
                } finally {
                    setSubmetendo(false);
                }
            },
            { textoConfirmar: isEdicao ? 'Confirmar Edição' : 'Confirmar Submissão', tipo: 'confirm' }
        );
    };

    // 🔥 Config visual do popup
    const popupConfig = {
        info: { icone: <FaInfoCircle />, cor: '#3b82f6', bgCor: 'rgba(59, 130, 246, 0.08)' },
        success: { icone: <FaCheckCircle />, cor: '#10b981', bgCor: 'rgba(16, 185, 129, 0.08)' },
        warning: { icone: <FaExclamationTriangle />, cor: '#f59e0b', bgCor: 'rgba(245, 158, 11, 0.08)' },
        danger: { icone: <FaExclamationTriangle />, cor: '#dc2626', bgCor: 'rgba(220, 38, 38, 0.08)' },
        confirm: { icone: <FaQuestionCircle />, cor: '#4f46e5', bgCor: 'rgba(79, 70, 229, 0.08)' }
    }[popup.tipo] || { icone: <FaInfoCircle />, cor: '#3b82f6', bgCor: 'rgba(59, 130, 246, 0.08)' };

    const estadoFormLower = String(submissaoForm.estado).toLowerCase();
    const alertaComprovativoObrigatorio = estadoFormLower === 'pago'
        && !submissaoFicheiro
        && !periodoSelecionado?.fecho?.submissaoFinancas?.comprovativoUrl;

    // =============================================
    // RENDER
    // =============================================
    return (
        <div className="fin-iva">
            <header className="fin-section-header" >
                <div className="fin-section-title">
                    <Icon name="receipt_long" size={24} color="var(--fin-primary)" />
                    <div>
                        <h2>Apuramento de IVA</h2>
                        <p>{subtituloPeriodo} • Análise detalhada por taxa e categoria</p>
                    </div>
                </div>
                <div className="fin-section-actions">
                    <span
                        className="fin-badge"
                        style={{
                            background: isISPC
                                ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(245, 158, 11, 0.05))'
                                : isIsento
                                    ? 'linear-gradient(135deg, rgba(107, 114, 128, 0.12), rgba(107, 114, 128, 0.04))'
                                    : 'linear-gradient(135deg, rgba(79, 70, 229, 0.15), rgba(79, 70, 229, 0.05))',
                            color: isISPC ? '#d97706' : isIsento ? '#6b7280' : 'var(--fin-primary)',
                            border: `2px solid ${isISPC ? 'rgba(245, 158, 11, 0.3)' : isIsento ? 'rgba(107, 114, 128, 0.2)' : 'rgba(79, 70, 229, 0.3)'}`,
                            padding: '0.4rem 0.85rem',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px'
                        }}
                        title={`Regime ${regimeIVA} — Submissão ${periodicidadeLabel}`}
                    >
                        <Icon name={isISPC ? 'schedule' : 'receipt'} size={12} />
                        Regime {regimeIVA} · {periodicidadeLabel} · {taxaLabel}
                    </span>

                    <select
                        value={tipo}
                        onChange={e => setTipo(e.target.value)}
                        className="fin-filter-select"
                        disabled={isISPC || isIsento}
                        title={
                            isISPC
                                ? 'Empresas ISPC apenas veem o consolidado do ano'
                                : isIsento
                                    ? 'Empresa isenta não tem apuramento de IVA'
                                    : ''
                        }
                    >
                        <option value="Mensal">Mensal</option>
                        <option value="Anual">Todos os Meses (Anual)</option>
                    </select>

                    {tipo === 'Mensal' && !isISPC && !isIsento && (
                        <select value={mes} onChange={e => setMes(parseInt(e.target.value))} className="fin-filter-select">
                            {meses.map((m, i) => <option key={i} value={i}>{m}</option>)}
                        </select>
                    )}

                    <select value={ano} onChange={e => setAno(parseInt(e.target.value))} className="fin-filter-select">
                        {[2023, 2024, 2025, 2026, 2027].map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                </div>
            </header>

            {loading ? (
                <div className="fin-loading"><div className="fin-spinner" />A carregar...</div>
            ) : isIsento ? (
                <div className="fin-empty">
                    <Icon name="block" size={48} color="var(--fin-muted)" />
                    <h3>Empresa Isenta de IVA</h3>
                    <p>Esta empresa está no regime Isento e não tem apuramento de IVA.</p>
                </div>
            ) : (
                <>
                    {isISPC && (
                        <div
                            className="fin-alerta"
                            style={{
                                background: 'rgba(245, 158, 11, 0.06)',
                                border: '2px solid',
                                padding: '0.75rem 1rem',
                                borderRadius: 'var(--fin-radius)',
                                marginBottom: '1rem',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '0.6rem'
                            }}
                        >
                            <Icon name="info" size={18} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                            <div style={{ flex: 1 }}>
                                <strong style={{ display: 'block', fontSize: '0.8rem', color: '#92400e' }}>
                                    Regime ISPC — Apuramento Trimestral a {TAXA_ISPC_LABEL}
                                </strong>
                                <span style={{ fontSize: '0.72rem', color: '#78350f', lineHeight: 1.5 }}>
                                    Esta empresa está no regime ISPC. <strong>Não cobra IVA nas faturas (0%)</strong> e
                                    paga <strong>{TAXA_ISPC_LABEL} sobre o Volume de Negócios</strong>, submetido a cada 3 meses
                                    — nos meses de <strong>Março, Junho, Setembro e Dezembro</strong>.
                                </span>
                            </div>
                        </div>
                    )}

                    {/* KPIs */}
                    <section className="fin-iva-cards">
                        {isISPC ? (
                            <>
                                <div className="fin-iva-card azul">
                                    <span>Volume de Negócios ({ano})</span>
                                    <strong>{fmt(r.baseTributavel || 0)}</strong>
                                    <small style={{ fontSize: '0.6rem', color: 'var(--fin-info-dark)', marginTop: '0.2rem' }}>
                                        {r.numeroFaturas || 0} faturas emitidas
                                    </small>
                                </div>
                                <div className="fin-iva-card laranja">
                                    <span>Taxa ISPC</span>
                                    <strong>{TAXA_ISPC_LABEL}</strong>
                                    <small style={{ fontSize: '0.6rem', color: 'var(--fin-info-dark)', marginTop: '0.2rem' }}>
                                        Sobre o volume de negócios
                                    </small>
                                </div>
                                <div className={`fin-iva-card ${ap.ivaAEntregar > 0 ? 'vermelho' : ''}`}>
                                    <span>Imposto a Entregar ({ano})</span>
                                    <strong>{fmt(ap.ivaAEntregar || 0)}</strong>
                                    <small style={{ fontSize: '0.6rem', color: 'var(--fin-info-dark)', marginTop: '0.2rem' }}>
                                        {trimestresISPC.filter(t => t.fechado).length} trimestre(s) fechado(s)
                                    </small>
                                </div>
                                <div className="fin-iva-card roxo">
                                    <span>Submissão</span>
                                    <strong>{deveSubmeterEsteMes ? 'Este mês' : 'Acumulando'}</strong>
                                    <small style={{ fontSize: '0.6rem', color: 'var(--fin-info-dark)', marginTop: '0.2rem' }}>
                                        {deveSubmeterEsteMes
                                            ? `${meses[mes]} fecha o T${trimestreAtual}`
                                            : `Próximo fecho: ${NOMES_FIM_TRIMESTRE.find((_, i) => MESES_FIM_TRIMESTRE[i] >= mes) || 'Dezembro'}`}
                                    </small>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="fin-iva-card azul">
                                    <span>IVA Liquidado (Vendas)</span>
                                    <strong>{fmt(ap.ivaLiquidado)}</strong>
                                </div>
                                <div className="fin-iva-card verde">
                                    <span>IVA Dedutível (Compras + Despesas)</span>
                                    <strong>{fmt(ap.ivaDedutivel)}</strong>
                                    <small style={{ fontSize: '0.6rem', color: 'var(--fin-info-dark)', marginTop: '0.2rem' }}>
                                        Despesas: {fmt(ap.ivaDedutivelDespesas)} | Stock: {fmt(ap.ivaDedutivelCompras)}
                                    </small>
                                </div>
                                <div className={`fin-iva-card ${ap.ivaAEntregar > 0 ? 'vermelho' : ''}`}>
                                    <span>IVA a Entregar</span>
                                    <strong>{fmt(ap.ivaAEntregar)}</strong>
                                </div>
                                <div className={`fin-iva-card ${ap.ivaARecuperar > 0 ? 'roxo' : ''}`}>
                                    <span>IVA a Recuperar</span>
                                    <strong>{fmt(ap.ivaARecuperar)}</strong>
                                </div>
                            </>
                        )}
                    </section>

                    {/* TABELA DE TRIMESTRES COM ESTADO DA SUBMISSÃO */}
                    {isISPC && trimestresISPC.length > 0 && (
                        <section className="fin-card">
                            <h3>
                                <Icon name="event_note" size={16} color="var(--fin-primary)" />
                                Apuramento por Trimestre ({ano})
                            </h3>
                            <div className="fin-table-wrapper">
                                <table className="fin-table">
                                    <thead>
                                        <tr>
                                            <th>Trimestre</th>
                                            <th>Meses</th>
                                            <th>Volume de Negócios</th>
                                            <th>Taxa ISPC</th>
                                            <th>Imposto a Entregar</th>
                                            <th>Estado</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {trimestresISPC.map((t) => {
                                            const mesesDoTri = {
                                                1: 'Jan, Fev, Mar',
                                                2: 'Abr, Mai, Jun',
                                                3: 'Jul, Ago, Set',
                                                4: 'Out, Nov, Dez'
                                            }[t.trimestre];

                                            const fecho = getFechoPorTrimestre(t.trimestre);
                                            const estado = estadoDoFecho(fecho);
                                            const badge = estadoVisual(estado);
                                            const isCompleto = estado === ESTADOS_SUBMISSAO.PAGO_COMPLETO;
                                            const faltaComp = estado === ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO;
                                            const temFecho = !!fecho?._id;
                                            const podeSubmeter = temFecho && !isCompleto;
                                            const isEdicao = estado === ESTADOS_SUBMISSAO.PENDENTE
                                                || estado === ESTADOS_SUBMISSAO.SUBMETIDO;

                                            return (
                                                <tr key={t.trimestre}>
                                                    <td><strong>T{t.trimestre}/{ano}</strong></td>
                                                    <td>{mesesDoTri}</td>
                                                    <td>{fmt(t.volume)}</td>
                                                    <td><strong>{TAXA_ISPC_LABEL}</strong></td>
                                                    <td style={{
                                                        fontWeight: 700,
                                                        color: t.fechado ? '#dc2626' : 'var(--fin-info-dark)'
                                                    }}>
                                                        {t.fechado ? fmt(t.imposto) : '—'}
                                                    </td>
                                                    <td>
                                                        <span className={`fin-badge ${badge.classe}`}>
                                                            <Icon name={badge.icone} size={11} />
                                                            {badge.label}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {podeSubmeter && (
                                                            <button
                                                                type="button"
                                                                onClick={() => abrirModalSubmissaoPeriodo(
                                                                    fecho,
                                                                    `T${t.trimestre}/${ano}`
                                                                )}
                                                                className={`fin-badge ${faltaComp ? 'badge-warning' : (isEdicao ? 'badge-warning' : 'badge-info')}`}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    border: 'none',
                                                                    fontFamily: 'inherit',
                                                                    fontWeight: 700,
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '0.25rem'
                                                                }}
                                                                title={
                                                                    faltaComp
                                                                        ? 'Falta anexar comprovativo'
                                                                        : (isEdicao ? 'Editar submissão pendente' : 'Registar submissão')
                                                                }
                                                            >
                                                                <Icon
                                                                    name={faltaComp ? 'cloud_upload' : (isEdicao ? 'edit' : 'upload')}
                                                                    size={11}
                                                                />
                                                                {faltaComp ? 'Anexar' : (isEdicao ? 'Editar' : 'Submeter')}
                                                            </button>
                                                        )}
                                                        {isCompleto && (
                                                            <span className="fin-badge badge-success" style={{ opacity: 0.85 }}>
                                                                <Icon name="lock" size={11} />
                                                                Bloqueado
                                                            </span>
                                                        )}
                                                        {!temFecho && (
                                                            <span className="fin-badge badge-muted">
                                                                <Icon name="hourglass_empty" size={11} />
                                                                Não fechado
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr style={{ background: 'rgba(245, 158, 11, 0.08)', fontWeight: 700 }}>
                                            <td colSpan="2"><strong>TOTAL ANO</strong></td>
                                            <td><strong>{fmt(r.baseTributavel || 0)}</strong></td>
                                            <td>—</td>
                                            <td style={{ color: '#dc2626' }}>
                                                <strong>{fmt(ap.ivaAEntregar || 0)}</strong>
                                            </td>
                                            <td colSpan="2"></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                            <p style={{
                                marginTop: '0.75rem',
                                fontSize: '0.7rem',
                                color: 'var(--fin-info-dark)',
                                fontStyle: 'italic',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.35rem'
                            }}>
                                <Icon name="info" size={12} />
                                No regime ISPC, o imposto é calculado apenas nos meses de
                                Março, Junho, Setembro e Dezembro, aplicando {TAXA_ISPC_LABEL} sobre o Volume de
                                Negócios acumulado do trimestre.
                            </p>
                        </section>
                    )}

                    {/* GRÁFICO */}
                    <section className="fin-card">
                        <h3>
                            <Icon name="analytics" size={16} color="var(--fin-primary)" />
                            {tituloGrafico}
                        </h3>
                        {dadosGrafico.length === 0 ? (
                            <p className="fin-vazio">Sem dados para o período</p>
                        ) : (
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={dadosGrafico}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="label" />
                                    <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} width={45} />
                                    <Tooltip formatter={(v) => fmt(v)} />
                                    <Legend />
                                    <Bar dataKey="base" fill="#3b82f6" name="Base" radius={[6, 6, 0, 0]} />
                                    <Bar
                                        dataKey="iva"
                                        fill={isISPC ? '#f59e0b' : '#ef4444'}
                                        name={isISPC ? `Imposto ISPC (${TAXA_ISPC_LABEL})` : 'IVA Liquidado'}
                                        radius={[6, 6, 0, 0]}
                                    />
                                    {tipo === 'Anual' && !isISPC && (
                                        <Bar dataKey="ivaDedutivel" fill="#10b981" name="IVA Dedutível" radius={[6, 6, 0, 0]} />
                                    )}
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </section>

                    {/* DETALHE POR TAXA */}
                    {!isISPC && (
                        <section className="fin-card">
                            <h3>Detalhe por Taxa</h3>
                            <div className="fin-table-wrapper">
                                <table className="fin-table">
                                    <thead>
                                        <tr>
                                            <th>Taxa</th>
                                            <th>Base Tributável</th>
                                            <th>IVA</th>
                                            <th>Nº Faturas</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ivaPorTaxa.length === 0 && (
                                            <tr><td colSpan="4" className="fin-vazio">Sem dados</td></tr>
                                        )}
                                        {ivaPorTaxa.map((t, i) => (
                                            <tr key={i}>
                                                <td><strong>{t.taxa}%</strong></td>
                                                <td>{fmt(t.base)}</td>
                                                <td>{fmt(t.iva)}</td>
                                                <td>{t.numeroFaturas}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* DECOMPOSIÇÃO IVA DEDUTÍVEL */}
                    {!isISPC && (
                        <section className="fin-card">
                            <h3>
                                <Icon name="receipt_long" size={16} color="var(--fin-primary)" />
                                Decomposição do IVA Dedutível
                            </h3>
                            <div className="fin-table-wrapper">
                                <table className="fin-table">
                                    <thead>
                                        <tr>
                                            <th>Origem</th>
                                            <th>IVA</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><strong>Despesas (documentos)</strong></td>
                                            <td>{fmt(ap.ivaDedutivelDespesas || 0)}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Compras de Stock (produtos c/ IVA)</strong></td>
                                            <td>{fmt(ap.ivaDedutivelCompras || 0)}</td>
                                        </tr>
                                        <tr style={{ background: 'rgba(16, 185, 129, 0.08)', fontWeight: 700 }}>
                                            <td><strong>TOTAL IVA DEDUTÍVEL</strong></td>
                                            <td>{fmt(ap.ivaDedutivel || 0)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* DETALHE MENSAL COM ESTADOS DE SUBMISSÃO */}
                    {tipo === 'Anual' && detalheMensal.length > 0 && (
                        <section className="fin-card">
                            <h3>
                                <Icon name="calendar_month" size={16} color="var(--fin-primary)" />
                                {isISPC
                                    ? `Detalhe Mensal — ISPC (${ano})`
                                    : `Detalhe Mensal de IVA (${ano})`}
                            </h3>
                            <div className="fin-table-wrapper">
                                <table className="fin-table">
                                    <thead>
                                        <tr>
                                            <th>Mês</th>
                                            <th>Base Tributável</th>
                                            <th>{isISPC ? 'Imposto ISPC' : 'IVA Liquidado'}</th>
                                            <th>IVA Dedutível</th>
                                            <th>{isISPC ? 'Trim.' : 'IVA a Entregar'}</th>
                                            <th>Nº Faturas</th>
                                            {!isISPC && <th>Estado</th>}
                                            {!isISPC && <th>Ações</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detalheMensal.map((m, i) => {
                                            const fecho = !isISPC ? getFechoPorMes(m.mesIndex) : null;
                                            const estado = !isISPC ? estadoDoFecho(fecho) : null;
                                            const badge = !isISPC ? estadoVisual(estado) : null;
                                            const isCompleto = !isISPC && estado === ESTADOS_SUBMISSAO.PAGO_COMPLETO;
                                            const faltaComp = !isISPC && estado === ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO;
                                            const temFecho = !isISPC && !!fecho?._id;
                                            const podeSubmeter = !isISPC && temFecho && !isCompleto;
                                            const isEdicao = !isISPC && (estado === ESTADOS_SUBMISSAO.PENDENTE
                                                || estado === ESTADOS_SUBMISSAO.SUBMETIDO);

                                            return (
                                                <tr key={i}>
                                                    <td><strong>{m.mesNome}</strong></td>
                                                    <td>{fmt(m.baseTributavel)}</td>
                                                    <td style={{
                                                        color: isISPC && m.isFimTrimestre ? '#d97706' : 'inherit',
                                                        fontWeight: isISPC && m.isFimTrimestre ? 700 : 400
                                                    }}>
                                                        {isISPC
                                                            ? (m.isFimTrimestre ? fmt(m.baseTributavel * TAXA_ISPC) : '—')
                                                            : fmt(m.ivaLiquidado)}
                                                    </td>
                                                    <td>{isISPC ? '—' : fmt(m.ivaDedutivel)}</td>
                                                    <td>
                                                        {isISPC
                                                            ? `T${m.trimestre}`
                                                            : (
                                                                <span style={{
                                                                    color: m.ivaAEntregar > 0 ? '#dc2626' : 'inherit',
                                                                    fontWeight: m.ivaAEntregar > 0 ? 700 : 400
                                                                }}>
                                                                    {fmt(m.ivaAEntregar)}
                                                                </span>
                                                            )}
                                                    </td>
                                                    <td>{m.numeroFaturas}</td>
                                                    {!isISPC && (
                                                        <td>
                                                            <span className={`fin-badge ${badge.classe}`}>
                                                                <Icon name={badge.icone} size={11} />
                                                                {badge.label}
                                                            </span>
                                                        </td>
                                                    )}
                                                    {!isISPC && (
                                                        <td>
                                                            {podeSubmeter && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => abrirModalSubmissaoPeriodo(
                                                                        fecho,
                                                                        `${m.mesNome} ${ano}`
                                                                    )}
                                                                    className={`fin-badge ${faltaComp ? 'badge-warning' : (isEdicao ? 'badge-warning' : 'badge-info')}`}
                                                                    style={{
                                                                        cursor: 'pointer',
                                                                        border: 'none',
                                                                        fontFamily: 'inherit',
                                                                        fontWeight: 700,
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: '0.25rem'
                                                                    }}
                                                                    title={
                                                                        faltaComp
                                                                            ? 'Falta anexar comprovativo'
                                                                            : (isEdicao ? 'Editar submissão pendente' : 'Registar submissão')
                                                                    }
                                                                >
                                                                    <Icon
                                                                        name={faltaComp ? 'cloud_upload' : (isEdicao ? 'edit' : 'upload')}
                                                                        size={11}
                                                                    />
                                                                    {faltaComp ? 'Anexar' : (isEdicao ? 'Editar' : 'Submeter')}
                                                                </button>
                                                            )}
                                                            {isCompleto && (
                                                                <span className="fin-badge badge-success" style={{ opacity: 0.85 }}>
                                                                    <Icon name="lock" size={11} />
                                                                    Bloqueado
                                                                </span>
                                                            )}
                                                            {!temFecho && (
                                                                <span className="fin-badge badge-muted">
                                                                    <Icon name="hourglass_empty" size={11} />
                                                                    Não fechado
                                                                </span>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                        <tr style={{
                                            background: isISPC
                                                ? 'rgba(245, 158, 11, 0.08)'
                                                : 'rgba(79, 70, 229, 0.08)',
                                            fontWeight: 700
                                        }}>
                                            <td><strong>TOTAL</strong></td>
                                            <td>{fmt(detalheMensal.reduce((acc, m) => acc + m.baseTributavel, 0))}</td>
                                            <td>
                                                {isISPC
                                                    ? fmt(detalheMensal.reduce((acc, m) => acc + (m.isFimTrimestre ? m.baseTributavel * TAXA_ISPC : 0), 0))
                                                    : fmt(detalheMensal.reduce((acc, m) => acc + m.ivaLiquidado, 0))}
                                            </td>
                                            <td>{isISPC ? '—' : fmt(detalheMensal.reduce((acc, m) => acc + m.ivaDedutivel, 0))}</td>
                                            <td>
                                                {isISPC
                                                    ? fmt(ap.ivaAEntregar || 0)
                                                    : fmt(detalheMensal.reduce((acc, m) => acc + m.ivaAEntregar, 0))}
                                            </td>
                                            <td>{detalheMensal.reduce((acc, m) => acc + m.numeroFaturas, 0)}</td>
                                            {!isISPC && <td colSpan="2"></td>}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* DESPESAS POR CATEGORIA */}
                    <section className="fin-card">
                        <h3>
                            {isISPC
                                ? 'Despesas por Categoria'
                                : 'Despesas por Categoria (para IVA dedutível)'}
                        </h3>
                        <div className="fin-table-wrapper">
                            <table className="fin-table">
                                <thead>
                                    <tr>
                                        <th>Categoria</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {despesasPorCat.length === 0 && (
                                        <tr><td colSpan="2" className="fin-vazio">Sem dados</td></tr>
                                    )}
                                    {despesasPorCat.map((c, i) => (
                                        <tr key={i}>
                                            <td><strong>{c.categoria}</strong></td>
                                            <td>{fmt(c.valor)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </>
            )}

            {/* ===== MODAL DE SUBMISSÃO ===== */}
            {modalSubmissaoAberto && periodoSelecionado && (
                <div
                    className="modal-overlay"
                    onClick={() => !submetendo && setModalSubmissaoAberto(false)}
                    style={{ zIndex: 99999 }}
                >
                    <div
                        className="modal-content modal-confirm"
                        onClick={e => e.stopPropagation()}
                        style={{ maxWidth: '560px' }}
                    >
                        <header className="modal-header">
                            <h3>
                                {periodoSelecionado.estado === ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO
                                    ? <><Icon name="cloud_upload" size={18} color="var(--fin-primary)" style={{ marginRight: '0.4rem' }} /> Anexar Comprovativo — {periodoSelecionado.tipoPeriodoLabel}</>
                                    : periodoSelecionado.estado === ESTADOS_SUBMISSAO.PENDENTE
                                        || periodoSelecionado.estado === ESTADOS_SUBMISSAO.SUBMETIDO
                                        ? <><Icon name="edit" size={18} color="var(--fin-primary)" style={{ marginRight: '0.4rem' }} /> Editar Submissão — {periodoSelecionado.tipoPeriodoLabel}</>
                                        : <><Icon name="add_box" size={18} color="var(--fin-primary)" style={{ marginRight: '0.4rem' }} /> Registar Submissão — {periodoSelecionado.tipoPeriodoLabel}</>
                                }
                                <span
                                    style={{
                                        marginLeft: '0.5rem',
                                        padding: '0.2rem 0.6rem',
                                        borderRadius: '100px',
                                        fontSize: '0.6rem',
                                        background: isISPC ? 'rgba(245, 158, 11, 0.15)' : 'rgba(79, 70, 229, 0.15)',
                                        color: isISPC ? '#d97706' : 'var(--fin-primary)',
                                        border: `1px solid ${isISPC ? 'rgba(245, 158, 11, 0.3)' : 'rgba(79, 70, 229, 0.3)'}`
                                    }}
                                >
                                    {tipoImposto}
                                </span>
                            </h3>
                            <button
                                className="modal-close"
                                onClick={() => !submetendo && setModalSubmissaoAberto(false)}
                                disabled={submetendo}
                            >
                                <FaTimes />
                            </button>
                        </header>

                        <div className="modal-body">
                            {periodoSelecionado.estado === ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO && (
                                <div style={{
                                    padding: '0.75rem 0.9rem',
                                    background: 'rgba(245, 158, 11, 0.08)',
                                    border: '1px solid rgba(245, 158, 11, 0.25)',
                                    borderLeft: '4px solid #f59e0b',
                                    borderRadius: 'var(--fin-radius)',
                                    marginBottom: '1rem',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '0.6rem'
                                }}>
                                    <Icon name="cloud_upload" size={20} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                                    <div>
                                        <strong style={{ display: 'block', fontSize: '0.8rem', color: '#92400e', marginBottom: '0.25rem' }}>
                                            Falta anexar o comprovativo de pagamento
                                        </strong>
                                        <span style={{ fontSize: '0.72rem', color: '#78350f', lineHeight: 1.5 }}>
                                            A submissão já foi marcada como <strong>PAGA</strong>, mas falta o
                                            comprovativo. Anexe o ficheiro abaixo para completar.
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div style={{
                                padding: '0.6rem 0.85rem',
                                background: isISPC
                                    ? 'rgba(245, 158, 11, 0.06)'
                                    : 'rgba(79, 70, 229, 0.06)',
                                borderRadius: 'var(--fin-radius)',
                                border: `1px solid ${isISPC ? 'rgba(245, 158, 11, 0.2)' : 'rgba(79, 70, 229, 0.2)'}`,
                                fontSize: '0.72rem',
                                color: isISPC ? '#92400e' : 'var(--fin-primary)',
                                marginBottom: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.35rem'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <FaInfoCircle />
                                    <strong>{descricaoImposto}</strong>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingLeft: '1.4rem' }}>
                                    <span>
                                        {isISPC
                                            ? `Regime ISPC — ${TAXA_ISPC_LABEL} sobre Volume de Negócios — Submissão ${periodicidadeImposto}`
                                            : `Regime Normal — IVA ${taxaLabel} — Submissão ${periodicidadeImposto}`
                                        }
                                    </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingLeft: '1.4rem' }}>
                                    <span>Período: <strong>{periodoSelecionado.tipoPeriodoLabel}</strong></span>
                                </div>
                            </div>

                            <div style={{
                                padding: '0.85rem 1rem',
                                background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.05), rgba(124, 58, 237, 0.03))',
                                borderRadius: 'var(--fin-radius)',
                                border: '1px solid rgba(79, 70, 229, 0.15)',
                                marginBottom: '1rem'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: '0.5rem',
                                    flexWrap: 'wrap',
                                    gap: '0.5rem'
                                }}>
                                    <span style={{
                                        fontSize: '0.6rem',
                                        fontWeight: 700,
                                        color: 'var(--fin-primary)',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        🧮 Cálculo Automático
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setSubmissaoForm({
                                            ...submissaoForm,
                                            valorPago: periodoSelecionado.valorCalculado
                                        })}
                                        style={{
                                            padding: '0.25rem 0.7rem',
                                            background: 'var(--fin-primary)',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '100px',
                                            fontSize: '0.6rem',
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            fontFamily: 'inherit'
                                        }}
                                    >
                                        ↻ Aplicar
                                    </button>
                                </div>

                                <div style={{
                                    background: 'rgba(79, 70, 229, 0.08)',
                                    padding: '0.6rem 0.8rem',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(79, 70, 229, 0.2)',
                                    textAlign: 'center'
                                }}>
                                    <div style={{
                                        fontSize: '0.55rem',
                                        color: 'var(--fin-primary)',
                                        textTransform: 'uppercase',
                                        fontWeight: 700,
                                        marginBottom: '0.15rem'
                                    }}>
                                        Valor a Submeter
                                    </div>
                                    <strong style={{
                                        fontSize: '1.1rem',
                                        color: 'var(--fin-primary)'
                                    }}>
                                        {fmt(periodoSelecionado.valorCalculado)}
                                    </strong>
                                </div>
                            </div>

                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.65rem'
                            }}>
                                <div>
                                    <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--fin-info-dark)', display: 'block', marginBottom: '0.25rem' }}>
                                        Referência / Nº Guia <span style={{ color: 'var(--fin-danger)' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={submissaoForm.referencia}
                                        onChange={e => setSubmissaoForm({ ...submissaoForm, referencia: e.target.value })}
                                        placeholder={isISPC ? 'Ex: ISPC-2026-T1' : 'Ex: IVA-2026-01'}
                                        style={{
                                            width: '100%',
                                            padding: '0.5rem 0.75rem',
                                            border: '1.5px solid var(--fin-border)',
                                            borderRadius: 'var(--fin-radius-sm)',
                                            fontSize: '0.75rem',
                                            fontFamily: 'inherit',
                                            background: 'var(--fin-white)',
                                            color: 'var(--fin-dark)',
                                            outline: 'none'
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                                    <div>
                                        <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--fin-info-dark)', display: 'block', marginBottom: '0.25rem' }}>
                                            Data de Submissão <span style={{ color: 'var(--fin-danger)' }}>*</span>
                                        </label>
                                        <input
                                            type="date"
                                            value={submissaoForm.dataSubmissao}
                                            onChange={e => setSubmissaoForm({ ...submissaoForm, dataSubmissao: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '0.5rem 0.75rem',
                                                border: '1.5px solid var(--fin-border)',
                                                borderRadius: 'var(--fin-radius-sm)',
                                                fontSize: '0.75rem',
                                                fontFamily: 'inherit',
                                                background: 'var(--fin-white)',
                                                color: 'var(--fin-dark)',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--fin-info-dark)', display: 'block', marginBottom: '0.25rem' }}>
                                            Data de Pagamento
                                        </label>
                                        <input
                                            type="date"
                                            value={submissaoForm.dataPagamento}
                                            onChange={e => setSubmissaoForm({ ...submissaoForm, dataPagamento: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '0.5rem 0.75rem',
                                                border: '1.5px solid var(--fin-border)',
                                                borderRadius: 'var(--fin-radius-sm)',
                                                fontSize: '0.75rem',
                                                fontFamily: 'inherit',
                                                background: 'var(--fin-white)',
                                                color: 'var(--fin-dark)',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                                    <div>
                                        <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--fin-info-dark)', display: 'block', marginBottom: '0.25rem' }}>
                                            Valor Pago
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={submissaoForm.valorPago}
                                            onChange={e => setSubmissaoForm({ ...submissaoForm, valorPago: e.target.value })}
                                            placeholder="0.00"
                                            style={{
                                                width: '100%',
                                                padding: '0.5rem 0.75rem',
                                                border: '1.5px solid var(--fin-border)',
                                                borderRadius: 'var(--fin-radius-sm)',
                                                fontSize: '0.75rem',
                                                fontFamily: 'inherit',
                                                background: 'var(--fin-white)',
                                                color: 'var(--fin-dark)',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--fin-info-dark)', display: 'block', marginBottom: '0.25rem' }}>
                                            Estado
                                        </label>
                                        <select
                                            value={submissaoForm.estado}
                                            onChange={e => setSubmissaoForm({ ...submissaoForm, estado: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '0.5rem 0.75rem',
                                                border: '1.5px solid var(--fin-border)',
                                                borderRadius: 'var(--fin-radius-sm)',
                                                fontSize: '0.75rem',
                                                fontFamily: 'inherit',
                                                background: 'var(--fin-white)',
                                                color: 'var(--fin-dark)',
                                                outline: 'none'
                                            }}
                                        >
                                            <option value="Pendente">Pendente</option>
                                            <option value="Submetido">Submetido</option>
                                            <option value="Pago">Pago</option>
                                            <option value="Isento">Isento</option>
                                        </select>
                                        {alertaComprovativoObrigatorio && (
                                            <small style={{
                                                fontSize: '0.6rem',
                                                color: '#d97706',
                                                marginTop: '0.3rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.25rem',
                                                fontWeight: 600
                                            }}>
                                                <Icon name="warning" size={11} color="#d97706" />
                                                Comprovativo obrigatório para estado "Pago"
                                            </small>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--fin-info-dark)', display: 'block', marginBottom: '0.25rem' }}>
                                        Observações
                                    </label>
                                    <textarea
                                        value={submissaoForm.observacoes}
                                        onChange={e => setSubmissaoForm({ ...submissaoForm, observacoes: e.target.value })}
                                        rows={2}
                                        placeholder="Notas adicionais..."
                                        style={{
                                            width: '100%',
                                            padding: '0.5rem 0.75rem',
                                            border: '1.5px solid var(--fin-border)',
                                            borderRadius: 'var(--fin-radius-sm)',
                                            fontSize: '0.75rem',
                                            fontFamily: 'inherit',
                                            background: 'var(--fin-white)',
                                            color: 'var(--fin-dark)',
                                            outline: 'none',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--fin-info-dark)', display: 'block', marginBottom: '0.25rem' }}>
                                        Comprovativo de Pagamento
                                    </label>
                                    <div style={{
                                        border: alertaComprovativoObrigatorio ? '2px dashed #f59e0b' : '2px dashed var(--fin-border)',
                                        borderRadius: 'var(--fin-radius-sm)',
                                        padding: '0.75rem',
                                        textAlign: 'center',
                                        background: alertaComprovativoObrigatorio ? 'rgba(245, 158, 11, 0.04)' : 'rgba(0, 0, 0, 0.01)'
                                    }}>
                                        <input
                                            type="file"
                                            id="comprovativo-input-iva"
                                            accept=".pdf,.jpg,.jpeg,.png,.webp"
                                            onChange={e => setSubmissaoFicheiro(e.target.files[0] || null)}
                                            hidden
                                        />
                                        <label
                                            htmlFor="comprovativo-input-iva"
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                cursor: 'pointer',
                                                fontSize: '0.75rem',
                                                color: alertaComprovativoObrigatorio ? '#d97706' : 'var(--fin-primary)',
                                                fontWeight: 600
                                            }}
                                        >
                                            <Icon
                                                name="cloud_upload"
                                                size={16}
                                                color={alertaComprovativoObrigatorio ? '#d97706' : 'var(--fin-primary)'}
                                            />
                                            <span>
                                                {submissaoFicheiro
                                                    ? submissaoFicheiro.name
                                                    : 'Clique para escolher (PDF, JPG, PNG)'}
                                            </span>
                                        </label>
                                        {submissaoFicheiro && (
                                            <button
                                                type="button"
                                                onClick={() => setSubmissaoFicheiro(null)}
                                                style={{
                                                    marginLeft: '0.5rem',
                                                    padding: '0.2rem 0.5rem',
                                                    background: 'rgba(220, 38, 38, 0.1)',
                                                    color: '#dc2626',
                                                    border: 'none',
                                                    borderRadius: '100px',
                                                    fontSize: '0.6rem',
                                                    fontWeight: 700,
                                                    cursor: 'pointer',
                                                    fontFamily: 'inherit'
                                                }}
                                            >
                                                <FaTimes /> Remover
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {periodoSelecionado.fecho?.submissaoFinancas?.comprovativoUrl && (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.5rem 0.75rem',
                                        background: 'rgba(16, 185, 129, 0.06)',
                                        borderRadius: 'var(--fin-radius-sm)',
                                        border: '1px solid rgba(16, 185, 129, 0.15)',
                                        fontSize: '0.72rem'
                                    }}>
                                        <Icon name="check_circle" size={14} color="#10b981" />
                                        <span style={{ color: '#059669', fontWeight: 600 }}>
                                            Comprovativo já anexado
                                        </span>
                                        <a
                                            href={`${API}${periodoSelecionado.fecho.submissaoFinancas.comprovativoUrl}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.3rem',
                                                color: 'var(--fin-primary)',
                                                fontWeight: 700,
                                                textDecoration: 'none'
                                            }}
                                        >
                                            Ver <FaExternalLinkAlt style={{ fontSize: '0.6rem' }} />
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button
                                className="modal-btn-secondary"
                                onClick={() => setModalSubmissaoAberto(false)}
                                disabled={submetendo}
                            >
                                Cancelar
                            </button>
                            <button
                                className="modal-btn-primary"
                                onClick={registarSubmissao}
                                disabled={submetendo || alertaComprovativoObrigatorio}
                                title={alertaComprovativoObrigatorio ? 'Anexe o comprovativo para continuar' : ''}
                                style={{
                                    background: 'var(--fin-gradient-premium)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.35rem'
                                }}
                            >
                                {submetendo
                                    ? 'A submeter...'
                                    : <>
                                        <Icon
                                            name={periodoSelecionado.estado === ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO ? 'cloud_upload' : 'check_circle'}
                                            size={14}
                                            color="#fff"
                                        />
                                        {periodoSelecionado.estado === ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO
                                            ? 'Anexar e Completar'
                                            : (periodoSelecionado.estado === ESTADOS_SUBMISSAO.PENDENTE || periodoSelecionado.estado === ESTADOS_SUBMISSAO.SUBMETIDO
                                                ? 'Confirmar Edição'
                                                : 'Confirmar Submissão')}
                                    </>
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== POPUP DO SISTEMA ===== */}
            {popup.aberto && (
                <div
                    className="modal-overlay popup-sistema-overlay"
                    onClick={() => {
                        if (popup.tipo !== 'confirm') fecharPopup();
                    }}
                    style={{ zIndex: 100000 }}
                >
                    <div
                        className="popup-sistema"
                        onClick={e => e.stopPropagation()}
                        style={{
                            maxWidth: '480px',
                            width: '100%',
                            background: 'var(--fin-white, #fff)',
                            borderRadius: '16px',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                            overflow: 'hidden',
                            animation: 'popupSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                        }}
                    >
                        <div style={{
                            padding: '1.5rem 1.5rem 1rem',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            gap: '0.75rem'
                        }}>
                            <div style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '50%',
                                background: popupConfig.bgCor,
                                color: popupConfig.cor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.75rem',
                                border: `2px solid ${popupConfig.cor}30`
                            }}>
                                {popupConfig.icone}
                            </div>
                            <h3 style={{
                                margin: 0,
                                fontSize: '1.15rem',
                                fontWeight: 800,
                                color: 'var(--fin-dark, #1a1a2e)',
                                letterSpacing: '-0.3px'
                            }}>
                                {popup.titulo}
                            </h3>
                        </div>

                        <div style={{
                            padding: '0 1.5rem 1.25rem',
                            textAlign: 'center'
                        }}>
                            <p style={{
                                margin: 0,
                                fontSize: '0.85rem',
                                color: 'var(--fin-info-dark, #6b7280)',
                                lineHeight: 1.55,
                                whiteSpace: 'pre-line'
                            }}>
                                {popup.mensagem}
                            </p>
                        </div>

                        <div style={{
                            padding: '1rem 1.5rem 1.25rem',
                            background: 'var(--fin-light, #f3f4f6)',
                            display: 'flex',
                            gap: '0.6rem',
                            justifyContent: 'center',
                            borderTop: '1px solid var(--fin-border, #e5e7eb)'
                        }}>
                            {popup.textoCancelar && (
                                <button
                                    onClick={popup.onCancelar || fecharPopup}
                                    style={{
                                        padding: '0.6rem 1.5rem',
                                        borderRadius: '100px',
                                        border: '1.5px solid var(--fin-border, #e5e7eb)',
                                        background: 'transparent',
                                        color: 'var(--fin-info-dark, #6b7280)',
                                        fontFamily: 'inherit',
                                        fontSize: '0.78rem',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        minWidth: '110px'
                                    }}
                                >
                                    {popup.textoCancelar}
                                </button>
                            )}
                            <button
                                onClick={popup.onConfirmar || fecharPopup}
                                style={{
                                    padding: '0.6rem 1.5rem',
                                    borderRadius: '100px',
                                    border: 'none',
                                    background: popupConfig.cor,
                                    color: '#fff',
                                    fontFamily: 'inherit',
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    boxShadow: `0 2px 10px ${popupConfig.cor}40`,
                                    minWidth: '110px'
                                }}
                            >
                                {popup.textoConfirmar}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes popupSlideIn {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
            `}</style>
        </div>
    );
}